<template>
    <!--
        Discord channel picker, hit after a successful bot-install OAuth
        round-trip. Fetches the text-channel list for the just-installed
        guild_id (the user OAuth token is parked server-side in
        BotInstallTokenStore for ~5 minutes) and submits the host's
        selection as enabled channels.

        Empty selection IS valid — it's the "pause /join" gesture per the
        save-channels contract.
    -->
    <v-container fluid class="channel-picker-container">
        <v-row>
            <v-col cols="12">
                <h1 class="channel-picker-title">
                    Pick channels where viewers can <code>/join</code>
                </h1>
                <p class="channel-picker-subtitle">
                    These are the text channels in guild
                    <code>{{ guildId }}</code> you can see. Pick the ones
                    where the ChatFight bot should respond to
                    <code>/join</code>.
                </p>
            </v-col>
        </v-row>

        <v-overlay v-model="loading" class="align-center justify-center" absolute>
            <v-progress-circular
                color="white"
                indeterminate
                size="64"
            ></v-progress-circular>
        </v-overlay>

        <v-row v-if="errorMessage">
            <v-col cols="12" md="8" offset-md="2">
                <v-alert type="error" :text="errorMessage"></v-alert>
                <v-btn class="mt-2" @click="goBack">Back to Account Settings</v-btn>
            </v-col>
        </v-row>

        <v-row v-if="!loading && !errorMessage">
            <v-col cols="12" md="8" offset-md="2">
                <v-card class="channel-picker-card">
                    <v-card-text>
                        <p v-if="channels.length === 0" class="no-channels">
                            No text channels visible in this guild. Either
                            the guild has none, or your account doesn't have
                            permission to view any.
                        </p>
                        <v-checkbox
                            v-for="ch in channels"
                            :key="ch.id"
                            v-model="selectedChannelIds"
                            :value="ch.id"
                            :label="formatChannelLabel(ch)"
                            density="compact"
                            hide-details
                            class="channel-checkbox"
                        ></v-checkbox>
                    </v-card-text>
                </v-card>
                <div class="picker-actions mt-3">
                    <v-btn @click="goBack">Cancel</v-btn>
                    <v-btn
                        color="#5865F2"
                        class="text-white ml-2"
                        :loading="saving"
                        @click="saveSelection"
                    >Save</v-btn>
                </div>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
export default {
    data() {
        return {
            loading: true,
            saving: false,
            errorMessage: '',
            channels: [],
            selectedChannelIds: [],
            // Display name for the guild, resolved server-side by
            // /install/channels. Echoed back into save-channels so the
            // persisted guild config carries a human-readable name.
            guildName: '',
        };
    },
    computed: {
        guildId() {
            // Vue Router exposes the query string as `$route.query`. The
            // bot-install callback redirects here with `?guild_id=...`.
            return this.$route?.query?.guild_id ?? '';
        },
    },
    async mounted() {
        if (!this.guildId) {
            this.errorMessage = 'Missing guild_id in URL — re-run the install flow.';
            this.loading = false;
            return;
        }
        try {
            const res = await fetch(
                `/api/discord/install/channels?guild_id=${encodeURIComponent(this.guildId)}`,
                { method: 'GET', credentials: 'include' },
            );
            if (res.status === 401) {
                this.$router?.replace('/login');
                return;
            }
            if (res.status === 410) {
                this.errorMessage =
                    'Bot install session expired — please re-run the install flow.';
                return;
            }
            if (!res.ok) {
                this.errorMessage = `Could not load channels (HTTP ${res.status}).`;
                return;
            }
            const data = await res.json();
            this.channels = Array.isArray(data?.channels) ? data.channels : [];
            this.guildName =
                typeof data?.guild_name === 'string' ? data.guild_name : '';
        } catch (err) {
            console.error('DiscordChannelPicker: fetch failed', err);
            this.errorMessage = 'Network error loading channels. Try again.';
        } finally {
            this.loading = false;
        }
    },
    methods: {
        formatChannelLabel(ch) {
            return ch.parent_id ? `# ${ch.name}` : `# ${ch.name}`;
        },
        goBack() {
            this.$router.push('/account-settings');
        },
        async saveSelection() {
            this.saving = true;
            try {
                // Pair each selected id with its display name from the
                // channel list so the server persists names, not just ids.
                const enabledChannels = this.selectedChannelIds.map((id) => {
                    const ch = this.channels.find((c) => c.id === id);
                    return {
                        channel_id: id,
                        channel_name: ch?.name ?? '',
                    };
                });
                const res = await fetch('/api/discord/install/save-channels', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        guild_id: this.guildId,
                        guild_name: this.guildName,
                        enabled_channels: enabledChannels,
                    }),
                });
                if (!res.ok) {
                    const body = await res.text().catch(() => '');
                    this.errorMessage = `Save failed (HTTP ${res.status}). ${body}`;
                    return;
                }
                this.$router.push('/account-settings');
            } catch (err) {
                console.error('DiscordChannelPicker: save failed', err);
                this.errorMessage = 'Network error saving channels. Try again.';
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.channel-picker-container {
    padding: 30px;
    padding-top: 80px;
    color: white;
}
.channel-picker-title {
    color: white;
    text-align: left;
}
.channel-picker-subtitle {
    color: rgba(255, 255, 255, 0.85);
    margin-top: 8px;
}
.channel-picker-card {
    background-color: rgba(0, 0, 0, 0.5) !important;
    color: white !important;
}
.channel-checkbox :deep(.v-label) {
    color: white !important;
    font-family: monospace;
}
.no-channels {
    color: rgba(255, 255, 255, 0.7);
    font-style: italic;
}
.picker-actions {
    display: flex;
    justify-content: flex-end;
}
</style>
